import React, { useEffect, useState, useCallback } from 'react';
import { adminApi } from '../../lib/adminApi';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { useToast } from '../../hooks/use-toast';
import { Plus, Pencil, Trash2, ExternalLink, Mail } from 'lucide-react';
import PostDialog from './PostDialog';

export const BlogManager = ({ onAuthError }) => {
  const { toast } = useToast();
  const [posts, setPosts] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => adminApi.get('/blog/all').then(({ data }) => setPosts(data.data)).catch(onAuthError), [onAuthError]);
  useEffect(() => { load(); }, [load]);

  const save = async (form) => {
    try {
      const payload = { ...form, notificar: form.notificar !== false };
      if (form.id) await adminApi.put(`/blog/${form.id}`, payload); else await adminApi.post('/blog', payload);
      toast({ title: form.id ? 'Artículo actualizado' : 'Artículo creado' });
      setEditing(null); load();
    } catch (err) {
      if (!onAuthError(err)) {
        const d = err.response?.data?.detail;
        toast({ title: 'Error', description: typeof d === 'string' ? d : 'Revisa título (3+), resumen (10+) y contenido (20+ caracteres).', variant: 'destructive' });
      }
      throw err;
    }
  };

  const sendTest = async (p) => {
    try {
      const { data } = await adminApi.post(`/blog/${p.id}/enviar-prueba`);
      toast({ title: 'Email de prueba enviado', description: data.message });
    } catch (err) { if (!onAuthError(err)) toast({ title: 'Error', description: 'No se pudo enviar la prueba.', variant: 'destructive' }); }
  };

  const remove = async (p) => {
    if (!window.confirm(`¿Eliminar el artículo "${p.titulo}"?`)) return;
    await adminApi.delete(`/blog/${p.id}`).catch(onAuthError);
    toast({ title: 'Artículo eliminado' }); load();
  };

  return (
    <section className="bg-white/5 border border-white/10 rounded-3xl p-6" data-testid="admin-blog">
      <div className="flex items-center justify-between mb-6">
        <div><h2 className="text-lg font-semibold">Blog de consejos</h2><p className="text-slate-400 text-sm">Artículos con URL amigable para Google. Solo los publicados se muestran en /blog.</p></div>
        <Button data-testid="blog-add-btn" onClick={() => setEditing({ titulo: '', resumen: '', contenido: '', imagen_url: '', autor: 'Equipo IwannaInsurance', categoria: 'Consejos', publicado: false, notificar: true, programado_para: null })} className="rounded-xl bg-blue-600 hover:bg-blue-500"><Plus className="h-4 w-4 mr-2" /> Nuevo artículo</Button>
      </div>
      <div className="space-y-2">
        {posts.map((p) => (
          <div key={p.id} data-testid="blog-admin-row" className={`flex items-center gap-4 rounded-2xl border p-3 ${p.publicado ? 'border-white/10 bg-slate-900/50' : 'border-dashed border-white/10 opacity-70'}`}>
            {p.imagen_url ? <img src={p.imagen_url} alt="" className="h-14 w-20 rounded-lg object-cover shrink-0" /> : <div className="h-14 w-20 rounded-lg bg-slate-800 shrink-0"></div>}
            <div className="flex-1 min-w-0">
              <p className="font-semibold truncate">{p.titulo}</p>
              <p className="text-xs text-slate-400 truncate">{p.categoria || 'Consejos'} · /blog/{p.slug} · {p.autor}{p.notificado_at && p.notificados != null ? ` · enviado a ${p.notificados}` : ''}</p>
            </div>
            <Badge className={`border-0 shrink-0 ${p.publicado ? 'bg-emerald-500/20 text-emerald-300' : p.programado_para ? 'bg-sky-500/20 text-sky-300' : 'bg-amber-500/20 text-amber-300'}`} data-testid="blog-status-badge">
              {p.publicado ? 'Publicado' : p.programado_para ? `Programado · ${new Date(`${p.programado_para}Z`).toLocaleString('es-VE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}` : 'Borrador'}
            </Badge>
            <div className="flex gap-1 shrink-0">
              {p.publicado && <a href={`/blog/${p.slug}`} target="_blank" rel="noreferrer" className="h-8 px-2 inline-flex items-center text-slate-300 hover:text-white" data-testid="blog-view-btn"><ExternalLink className="h-4 w-4" /></a>}
              <Button size="sm" variant="ghost" onClick={() => sendTest(p)} title="Enviarme el email de este artículo como prueba" className="h-8 px-2 hover:bg-white/10 hover:text-white" data-testid="blog-test-email-btn"><Mail className="h-4 w-4" /></Button>
              <Button size="sm" variant="ghost" onClick={() => setEditing(p)} className="h-8 px-2 hover:bg-white/10 hover:text-white" data-testid="blog-edit-btn"><Pencil className="h-4 w-4" /></Button>
              <Button size="sm" variant="ghost" onClick={() => remove(p)} className="h-8 px-2 text-red-300 hover:bg-red-500/10 hover:text-red-200" data-testid="blog-delete-btn"><Trash2 className="h-4 w-4" /></Button>
            </div>
          </div>
        ))}
        {posts.length === 0 && <p className="text-slate-500 text-sm">Aún no hay artículos.</p>}
      </div>
      <PostDialog post={editing} onClose={() => setEditing(null)} onSave={save} />
    </section>
  );
};

export default BlogManager;
